import { useMemo, Fragment } from 'react';
import { useTranslations } from 'next-intl';
import styles from '../styles/InterventionInfo.module.scss';
import SpeciesPlanted from './microComponents/SpeciesPlanted';
import SampleTreesInfo from './microComponents/SampleTreesInfo';
import TreeMapperBrand from './microComponents/TreeMapperBrand';
import ImageSlider from './ImageSlider';
import MobileInfoSwiper from '../../MobileInfoSwiper';
import OtherInterventionMetadata from './microComponents/OtherInterventionMetadata';
import OtherInterventionInfoHeader from './microComponents/OtherInterventionInfoHeader';
import { prepareInterventionMetadata } from '../../../../utils/projectV2';
import { useInterventionStore } from '../../../../stores';

interface Props {
  isMobile: boolean;
}

const OtherInterventionInfo = ({ isMobile }: Props) => {
  const selectedIntervention = useInterventionStore((state) =>
    state.selectedIntervention?.type !== 'single-tree-registration' &&
    state.selectedIntervention?.type !== 'multi-tree-registration'
      ? state.selectedIntervention
      : null
  );

  const hoveredIntervention = useInterventionStore((state) =>
    state.hoveredIntervention?.type !== 'single-tree-registration' &&
    state.hoveredIntervention?.type !== 'multi-tree-registration'
      ? state.hoveredIntervention
      : null
  );
  const interventionInfo = hoveredIntervention || selectedIntervention;
  const tProjectDetails = useTranslations('ProjectDetails');

  const sampleTrees = interventionInfo?.sampleInterventions || [];
  const plantedSpecies = interventionInfo?.plantedSpecies || [];
  const hasSampleTrees = sampleTrees.length > 0;
  const hasPlantedSpecies = plantedSpecies.length > 0;

  const { totalTreesCount } = useMemo(() => {
    const totalTreesCount = hasPlantedSpecies
      ? plantedSpecies.reduce(
          (sum, species: { treeCount: number }) => sum + species.treeCount,
          0
        )
      : 0;
    return { totalTreesCount };
  }, [interventionInfo]);

  const sampleInterventionSpeciesImages = useMemo(() => {
    if (hasSampleTrees) {
      const result = sampleTrees.map((item) => {
        return {
          id: item.coordinates[0].id,
          image: item.coordinates[0].image ?? '',
          description: tProjectDetails('sampleTreeTag', { tag: item.tag ?? '' }),
        };
      });
      return result;
    }
  }, [interventionInfo]);

  if (!interventionInfo) return null;

  const interventionMetadata = prepareInterventionMetadata(interventionInfo);
  const hasInterventionMetadata = interventionMetadata.length > 0;
  const plantDate =
    interventionInfo.interventionStartDate || interventionInfo.plantDate;

  const shouldDisplayImageCarousel =
    sampleInterventionSpeciesImages !== undefined &&
    sampleInterventionSpeciesImages?.length > 0;

  const content = [
    <Fragment key="interventionHeaderAndImage">
      <OtherInterventionInfoHeader
        hid={interventionInfo.hid}
        interventionType={interventionInfo.type}
        plantDate={plantDate}
        key="interventionHeader"
      />
      {shouldDisplayImageCarousel && (
        <ImageSlider
          key="imageSlider"
          images={sampleInterventionSpeciesImages}
          type="coordinate"
          isMobile={isMobile}
          imageSize="large"
          allowFullView={!isMobile}
        />
      )}
    </Fragment>,
    hasInterventionMetadata && (
      <OtherInterventionMetadata
        key="plantingDetails"
        metadata={interventionMetadata}
      />
    ),
    hasPlantedSpecies && (
      <SpeciesPlanted
        key="speciesPlanted"
        totalTreesCount={totalTreesCount}
        plantedSpecies={plantedSpecies}
        plantProject={selectedIntervention?.plantProject}
      />
    ),
    hasSampleTrees && (
      <SampleTreesInfo key="sampleTrees" sampleTrees={sampleTrees} />
    ),
  ].filter(Boolean);

  return isMobile ? (
    <>
      <MobileInfoSwiper
        slides={content}
        uniqueKey={interventionInfo.hid || ''}
      />
    </>
  ) : (
    <section className={styles.interventionInfoSection}>
      {content}
      <TreeMapperBrand />
    </section>
  );
};

export default OtherInterventionInfo;
